import { useGetMediaFoldersQuery } from "@api/media-folders";
import {
  MediaLibAddFolderBtn,
  MediaLibCategoryEnum,
  MediaLibFolder,
  MediaLibFoldersBreadcrumbs,
  useMediaLibPanelStoreShallow,
} from "@editor/modules/media-lib";
import { useMemo, useState } from "react";
import { MediaLibPanelFoldersSkeleton } from "./media-lib-panel-folders-skeleton";

export const MediaLibPanelFolders = () => {
  const [showAllFolders, setShowAllFolders] = useState(false);
  const {
    selectedFolderId,
    category,
    setSelectedFolderId,
    archiveFilesInFolders,
  } = useMediaLibPanelStoreShallow((s) => ({
    archiveFilesInFolders: s.archiveFilesInFolders,
    selectedFolderId: s.selectedFolderId,
    setSelectedFolderId: s.setSelectedFolderId,
    category: s.category,
  }));

  const isArchive = category === MediaLibCategoryEnum.Archive;
  const { data: allFolders = [], isFetching: isLoading } =
    useGetMediaFoldersQuery(
      {
        archived: isArchive,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  const canAddFolder = ![
    MediaLibCategoryEnum.Website,
    MediaLibCategoryEnum.Import,
    MediaLibCategoryEnum.Archive,
  ].includes(category);

  const selectedFolder = useMemo(() => {
    return selectedFolderId
      ? allFolders.find((f) => f._id === selectedFolderId)
      : null;
  }, [allFolders, selectedFolderId]);

  const folderParentId = useMemo(() => {
    if (selectedFolder) return selectedFolder._id;
    if (
      [
        MediaLibCategoryEnum.BrandAssets,
        MediaLibCategoryEnum.Website,
        MediaLibCategoryEnum.Import,
      ].includes(category)
    ) {
      return category;
    }

    return null;
  }, [allFolders, selectedFolder, category]);

  const folders = useMemo(() => {
    if (!folderParentId) {
      return allFolders
        .filter((f) => !f.parentId && (isArchive ? f.archived : !f.archived))
        .slice(0, showAllFolders ? undefined : 6);
    }

    return allFolders.filter((f) => {
      return (
        f.parentId === folderParentId && (isArchive ? f.archived : !f.archived)
      );
    });
  }, [allFolders, folderParentId, showAllFolders, isArchive]);

  return (
    <div className="tw-pb-6">
      <MediaLibFoldersBreadcrumbs
        className="tw-mb-4 tw-pl-6 tw-pr-5 -sm:tw-pl-3 -sm:tw-pr-2"
        folders={allFolders}
        selectedFolderId={selectedFolderId}
        setSelectedFolderId={setSelectedFolderId}
      />
      {!(folders.length === 0 && !canAddFolder) && (
        <>
          <div className="tw-mb-4 tw-flex tw-w-full tw-items-center tw-justify-between tw-pl-6 tw-pr-5 -sm:tw-pl-3 -sm:tw-pr-2 tw-text-[11px] tw-font-medium">
            <span>Folders</span>
            {canAddFolder && folders.length > 0 && (
              <MediaLibAddFolderBtn
                folderParentId={folderParentId}
                className="tw-m-0 tw-border-0 tw-bg-white tw-p-0 tw-outline-none [&_i]:tw-mr-0.5 [&_i]:tw-text-xs"
              />
            )}
          </div>

          <ol className="tw-m-0 tw-grid tw-list-none tw-grid-cols-3 tw-gap-2 tw-pl-6 tw-pr-5 -sm:tw-grid-cols-2 -sm:tw-pl-3 -sm:tw-pr-2">
            {isLoading ? (
              <MediaLibPanelFoldersSkeleton />
            ) : (
              <>
                {folders.map((folder) => (
                  <MediaLibFolder
                    key={folder._id}
                    folder={folder}
                    setSelectedFolderId={setSelectedFolderId}
                    archiveFilesInFolders={archiveFilesInFolders}
                  />
                ))}

                {canAddFolder && !folders.length && (
                  <MediaLibAddFolderBtn folderParentId={folderParentId} />
                )}
              </>
            )}
          </ol>
          {!showAllFolders && allFolders.length > 6 && !folderParentId && (
            <div className="tw-px-6 tw-pt-2.5">
              <button
                onClick={() => setShowAllFolders(true)}
                className="tw-m-0 tw-p-0 tw-text-[11px] tw-font-semibold tw-text-blue-500 tw-outline-none tw-transition-colors hover:tw-text-primary"
              >
                See all
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
